import React from "react"
import { useTranslation } from "react-i18next"

const INLINE_MARKDOWN = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*]+)\*\*|(https?:\/\/[^\s)<]+)/g
const MARKDOWN_ESCAPE = /\\([\\`*_{}[\]()#+\-.!|>~])/g
const HEADING = /^#{1,6}\s+(.*)$/

const STRUCTURAL_ESCAPE = /\\([[\]*()])/g

const unescapeMarkdown = (text) => text.replace(MARKDOWN_ESCAPE, "$1")

export const renderMarkdown = (text) =>
  text.split("\n").flatMap((line, index) => {
    const heading = line.match(HEADING)
    const content = heading ? (
      <strong key={`h${index}`} className="text-slate-900">
        {renderInline(heading[1])}
      </strong>
    ) : (
      renderInline(line)
    )
    return index === 0 ? [content] : ["\n", content]
  })

const CURRENCY_SYMBOLS = { EUR: "€", USD: "$", UAH: "₴", MDL: "L", RON: "lei " }

const formatAmount = (value) =>
  String(Math.round(Number(value))).replace(/\B(?=(\d{3})+(?!\d))/g, " ")

const renderInline = (source) => {
  const text = source.replace(STRUCTURAL_ESCAPE, "$1")
  const parts = []
  let last = 0
  let match

  INLINE_MARKDOWN.lastIndex = 0
  while ((match = INLINE_MARKDOWN.exec(text)) !== null) {
    if (match.index > last) parts.push(unescapeMarkdown(text.slice(last, match.index)))

    const [whole, label, labelUrl, bold, bareUrl] = match
    const url = unescapeMarkdown(labelUrl || bareUrl || "")
    if (url) {
      parts.push(
        <a
          key={match.index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline hover:text-blue-800 break-all"
        >
          {label || url}
        </a>
      )
    } else if (bold) {
      parts.push(<strong key={match.index}>{unescapeMarkdown(bold)}</strong>)
    } else {
      parts.push(whole)
    }
    last = match.index + whole.length
  }

  if (last < text.length) parts.push(unescapeMarkdown(text.slice(last)))
  return parts
}

export const OfferCard = ({ offer }) => {
  const { t } = useTranslation()
  const [minNights, maxNights] = offer.requested_nights || []
  const nights = Number(offer.nights)
  const nightsDiffer = minNights != null && (nights < minNights || nights > maxNights)
  const clientSymbol = CURRENCY_SYMBOLS[offer.client_currency] || `${offer.client_currency || ""} `

  return (
    <div className="ai_chat_bot_offer border border-slate-200 rounded-lg p-3 bg-white shadow-sm">
      <div className="font-semibold text-slate-800">{offer.hotel_name || "—"}</div>
      <div className="text-sm text-slate-500">
        {[offer.resort_name, offer.star_name].filter(Boolean).join(" · ")}
      </div>
      <div className="text-sm text-slate-500">
        {offer.from_date} — {offer.to_date}{" "}
        <span className={nightsDiffer ? "text-orange-600 font-medium" : ""}>
          ({offer.nights} {t("admin.ai_chat_bot_nights", "nights")})
        </span>
      </div>
      {(offer.room_type || offer.board) && (
        <div className="text-sm text-slate-500">{[offer.room_type, offer.board].filter(Boolean).join(" · ")}</div>
      )}
      <div className="mt-1 flex items-center justify-between gap-2">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-medium text-slate-900">
            {offer.price != null ? `${formatAmount(offer.price)} ${offer.currency || ""}`.trim() : "—"}
          </span>
          {offer.client_price != null && offer.client_currency !== offer.currency && (
            <span className="text-sm text-slate-500">
              ≈ {clientSymbol}
              {formatAmount(offer.client_price)}
            </span>
          )}
          {offer.within_budget === true && (
            <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
              {t("admin.ai_chat_bot_in_budget", "in budget")}
            </span>
          )}
          {offer.within_budget === false && (
            <span className="text-[11px] px-1.5 py-0.5 rounded bg-orange-50 text-orange-700">
              {t("admin.ai_chat_bot_over_budget", "over budget")}
            </span>
          )}
        </div>
        {offer.book_url && (
          <a
            href={offer.book_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-medium px-2.5 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-700"
          >
            {t("admin.ai_chat_bot_book", "Book")}
          </a>
        )}
      </div>
    </div>
  )
}
